import DOMPurify from 'dompurify'
import _ from 'es-toolkit/compat'
import parse from 'html-react-parser'

import checkFunctions from './check'

export const countWords = (text: string) => {
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '').length
}

const truncateByWords = (original: string, wordLimit: number, addEllipsis = true) => {
  const words = original.trim().split(/\s+/)
  if (words.length <= wordLimit) return original
  return (
    words.slice(0, wordLimit).join(' ') + (addEllipsis && words.length > wordLimit ? '...' : '')
  )
}

const truncateByChars = (original: string, charLimit: number, addEllipsis = true) => {
  if (original.length <= charLimit) return original
  return original.slice(0, charLimit) + (addEllipsis ? '...' : '')
}

/**
 * Utility to get truncated text based on word or character count.
 */
export const getTruncatedText = (
  text: string,
  options: { maxWords?: number; maxChars?: number; addEllipsis?: boolean },
) => {
  const addEllipsis = options.addEllipsis !== false

  if (options.maxWords) return truncateByWords(text, options.maxWords, addEllipsis)
  if (options.maxChars) return truncateByChars(text, options.maxChars, addEllipsis)
  return text
}

// Legacy function for backward compatibility
export const truncate = (text: string, maxWords: number) => {
  return truncateByWords(text, maxWords, false)
}

/**
 * Check if string is empty by removing spaces and backslash
 */

export const isEmpty = (str = '') => !str.replace(/\s+/g, ' ').trim()

export const sanitizeHTML = (html: string): string =>
  DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })

/**
 * Sanitize and parse html
 * @param {*} html
 * @param {*} opts
 * @returns
 */
export const html = ({ _html, opts = {} }) => {
  return parse(DOMPurify.sanitize(_html, { USE_PROFILES: { html: true } }), {
    ...opts,
  })
}

export const minWords = (text: string, min: number) => {
  if (countWords(text) < min) {
    return false
  }
  return true
}

export const maxWords = (text: string, max: number) => {
  if (countWords(text) > max) {
    return false
  }
  return true
}

export const validateInputCheck = (checks: any[] | undefined, value: string | string[]) => {
  if (!checks) return null
  for (const check of checks) {
    const checkFunction = checkFunctions[check.name]
    if (checkFunction) {
      const validated = checkFunction(value, check.params)
      if (!validated) return check
    }
  }
  return null
}

export const hasLessThanChars = (values: string[], numberChars: number) => {
  return values.some((value) => value.replaceAll(' ', '').length < numberChars)
}

export const getExtractedContent = (version: object, path: string) => {
  const content = _.get(version, path)
  return typeof content === 'string' ? content : ''
}

export const strToB64 = (value: string): string => {
  if (!value) return value
  const encoded = new TextEncoder().encode(value)
  const charCodes = Array.from(encoded)
  const base64String = String.fromCharCode(...charCodes)
  return btoa(base64String)
}

export const b64ToStr = (value: string): string => {
  if (!value) return value

  const strValue = atob(value)
  return new TextDecoder('utf-8').decode(
    Uint8Array.from({ length: strValue.length }, (element, index) => strValue.charCodeAt(index)),
  )
}

// replace all image tags with an emoji 🖼️
export const replaceImageTags = (content?: string, replacement = '🖼️') => {
  return content?.replace(/<img[^>]*>/g, replacement)
}

/**
 * Strip <ruby>...<rt>...</rt></ruby> tags, keeping only the base text.
 * Used in views that cannot render ruby annotations (e.g. batch item list cards).
 */
export { stripRubyTags } from '@core/components/editor/editor-v2/plugins/ruby-plugin'
